import axios from "axios";
import beveatApiResourceOwner from "../../../api/resource_owner_beveat_api.js";
import beveatApiStaging from "../../../api/staging_beveat_api.js";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ComingSoonModal from "../../components/coming_soon_modal.jsx";
import { commonConfigs, urls } from "../../../../config.js";
import ComboApiCatalogueCard from "./combo_api_catalogue_card.jsx";
import ProjectApiCatalogueCard from "../components/project_api_catalogue_card.jsx";
import PaymentsApiCatalogueCard from "./payments_api_catalogue_card.jsx";
import { getUser } from "../../../services/local_service.js";
import { INVOICE_PLAN_SYSCODE } from "../../../constants/invoice_plan_syscode.jsx";
import RenewProjectModal from "./renew_project_modal.jsx";
// import { DEVELOPER_PROJECT_STATUS } from "../../../../../commons/constants/developerProjStatus";
import { InvoiceCategorySequenceString, INVOICE_CATEGORY_SEQUENCE } from "../../../constants/invoice_category_sequence.jsx";

export default function CertificationGoLivePlan({ currentTab, isMobile }) {
  const [isOpen, setOpen] = useState(false);
  const [isReuseProjModalOpen, setReuseProjModalOpen] = useState(false);
  const [invoicePlanSyscode, setInvoicePlanSyscode] = useState(null);
  const [certificationPlans, setCertificationPlans] = useState([]);
  const [developerPlan, setDeveloperPlan] = useState([]);
  const [devProj, setDevProject] = useState({});
  const [comingSoonDate, setComingSoonDate] = useState(null);
  const [industryApiOptions, setIndustryApiOptions] = useState([]);
  const [organisationApiOptions, setOrganisationApiOptions] = useState([]);
  const navigate = useNavigate();
  const ssoId = getUser()?.sso_id;
  const getRGProjectDatasUrl = urls.baseUrl + commonConfigs.apiUrls.getProjects();
  const getCertificationPlansUrl = urls.resourceOwnerUrl + commonConfigs.apiUrls.getCertificationPlans();

  const handleComingSoonClick = (date) => {
    setComingSoonDate(date);
    setOpen(true);
  };

  const customOrder = [
    "nets qr",
    "enets",
    "nets click (android)",
    "nets click (ios)",
  ];

  const sortByCustomOrder = (plans) => {
    return [...plans].sort((a, b) => {
      const aIndex = customOrder.indexOf(a.invoice_plan.toLowerCase());
      const bIndex = customOrder.indexOf(b.invoice_plan.toLowerCase());
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    });
  };

  const isNetsCard = (plan) => {
    const name = plan.invoice_plan.toLowerCase();
    return (
      ["nets qr", "enets", "nets click (android)", "nets click (ios)"].includes(name) &&
      currentTab.invoiceSyscode === plan.invoice_syscode // Optional stricter match
    );
  };

  const groupByCategory = (plans) => {
    const grouped = plans.reduce((acc, item) => {
      const catName = item.invoice_plan_category;
      if (!acc[catName]) {
        acc[catName] = [];
      }
      acc[catName].push(item);
      return acc;
    }, {});

    // New rule-based scoring function using string match from constants
    const scoreCategory = (name) => {
      const lowerName = name.toLowerCase();

      for (const [key, value] of Object.entries(InvoiceCategorySequenceString)) {
        if (value.toLowerCase() === lowerName) {
          return Number(key); // category ID
        }
      }
      return 999; // fallback
    };

    // Sort based on category score
    const sorted = {};
    Object.keys(grouped)
      .sort((a, b) => scoreCategory(a) - scoreCategory(b))
      .forEach((key) => {
        sorted[key] = grouped[key];
      });

    return sorted;
  };

  const getBenefits = (industry) => {
    switch (industry) {
      case "Food Services":
        return [
          "Full Stack Developer Coursework",
          "OPENAPIPAAS Product, Voice & NETS Online Payment APIs",
          "Order-Fulfillment APIs",
        ];
      case "Air Transport":
        return [
          "Full Stack Developer Coursework",
          "OPENAPIPAAS Product, Voice & NETS Online Payment APIs",
          "Order-Fulfillment APIs",
          "Drone Traffic Management APIs",
        ];
      case "NETS QR":
        return [
          "2 Months Plan",
          "Includes NETS Transaction Validation and Tech Support",
          {
            title: "NETS Online Payment APIs",
            children: ["NETS QR"],
          },
        ];
      case "eNETS":
        return [
          "2 Months Plan",
          "Includes NETS Transaction Validation and Tech Support",
          {
            title: "NETS Online Payment APIs",
            children: ["eNETS"],
          },
        ];
      case "NETS Click (Android)":
        return [
          "2 Months Plan",
          "Includes NETS Transaction Validation and Tech Support",
          {
            title: "NETS Online Payment APIs",
            children: ["NETS Click"],
          },
        ];
      case "NETS Click (IOS)":
        return [
          "2 Months Plan",
          "Includes NETS Transaction Validation and Tech Support",
          {
            title: "NETS Online Payment APIs",
            children: ["NETS Click"],
          },
        ];
      case "Per Go Live Certification":
        return [
          "2 Months Plan",
          "Includes NETS Transaction Validation and Tech Support",
          {
            title: "NETS Online Payment APIs",
            children: ["NETS QR", "eNETS", "NETS Click"],
          },
        ];
      default:
        return [
          "Full Stack Developer Coursework",
          "OPENAPIPAAS Product, Voice & NETS Online Payment APIs",
          "Order-Fulfillment APIs",
        ];
    }
  };

  async function getRGProjectData() {
    await beveatApiStaging
      .get(getRGProjectDatasUrl, { headers: commonConfigs.resourceOwnerApiHeader })
      .then((res) => {
        const projects = res.data.result.data;
        const industryApiOptions = projects.filter(
          (project) =>
            project.project_billing_config_id == 1 &&
            project.project_name.toLowerCase().includes("industry")
        );

        const organisationApiOptions = projects.filter(
          (project) =>
            project.project_billing_config_id == 1 &&
            !project.project_name.toLowerCase().includes("industry")
        );

        setIndustryApiOptions(industryApiOptions);
        setOrganisationApiOptions(organisationApiOptions);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  async function getAllCertificationPlans(currentTab) {
    await beveatApiResourceOwner
      .get(getCertificationPlansUrl, {
        headers: commonConfigs.resourceOwnerApiHeader,
      })
      .then((res) => {
        // console.log(res.data.result.data);
        let plans = res.data.result.data.filter((item) => item.invoice_plan_syscode === currentTab.invoicePlanSyscode && item.invoice_syscode === currentTab.invoiceSyscode);
        let groupedPlans = groupByCategory(plans);
        setCertificationPlans(groupedPlans);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  async function getDeveloperProjectByPlan(planId) {
    const getDeveloperProjectByPlanUrl =
      urls.resourceOwnerUrl +
      commonConfigs.apiUrls.getDeveloperProjectByPlan(ssoId, planId);
    return await beveatApiResourceOwner
      .get(getDeveloperProjectByPlanUrl, {
        headers: commonConfigs.resourceOwnerApiHeader,
      })
      .then((res) => {
        setDevProject(res.data.result.data);
        return res.data.result.data;
      })
      .catch((err) => {
        console.log(err);
      });
  }

  useEffect(() => {
    getRGProjectData().catch((err) => {
      console.log("err for staging projects: " + err);
    });
    getAllCertificationPlans(currentTab).catch((err) => {
      console.log("err for certification plans: " + err);
    });
  }, []);

  const handleApplyClick = async (plan) => {
    setDeveloperPlan(plan);
    let environment;
    // let invoicePlanSyscode = plan.invoice_plan_syscode;

    // if (currentTab.name === "Certification") {
    //   const isGoLive =
    //     plan.certification_category.includes("Go Live");
    //   environment = isGoLive
    //     ? { value: "1", label: "Production" }
    //     : { value: "0", label: "Staging" };
    //   invoicePlanSyscode = isGoLive
    //     ? INVOICE_PLAN_SYSCODE.Certification_Plan_Go_Live
    //     : INVOICE_PLAN_SYSCODE.Certification_Plan_UAT;
    // } else {
    //   const isProduction = currentTab.name !== "Staging";
    //   environment = isProduction
    //     ? { value: "1", label: "Production" }
    //     : { value: "0", label: "Staging" };
    //   invoicePlanSyscode = isProduction
    //     ? INVOICE_PLAN_SYSCODE.API_Plan_Go_Live
    //     : INVOICE_PLAN_SYSCODE.API_Plan_UAT;
    // }
    // setInvoicePlanSyscode(invoicePlanSyscode);
    // const developerProject = await getDeveloperProjectByPlan(
    //   plan.org_invocie_plan_id
    // ).catch((err) => {
    //   console.log("err for getting inactiveDevProject: " + err);
    // });
    // if (Object.keys(developerProject).length !== 0 && developerProject.developer_proj_status !== DEVELOPER_PROJECT_STATUS.Active) {
    //   setReuseProjModalOpen(true);
    // } else {
    const projectNamesString = plan.project_name_preset;
    const projectNames = projectNamesString
      .split("|")
      .map((name) => name.toLowerCase());
    const industryApis = industryApiOptions
      .filter((project) => projectNames.includes(project.project_name))
      .map((project) => ({
        value: project.project_id,
        label: project.project_name,
      }));
    const organisationApis = organisationApiOptions
      .filter((project) => projectNames.includes(project.project_name))
      .map((project) => ({
        value: project.project_id,
        label: project.project_name
      }));

    const presetOptions = {
      // environment: environment,
      invoicePlanSyscode: plan.invoice_plan_syscode,
      invoiceRateSyscode: plan.invoice_rate_syscode,
      invoiceSyscode: plan.invoice_syscode,
      invoicePlan: plan.invoice_plan,
      industryApiOptions: industryApis,
      organisationApiOptions: organisationApis,
      subscriptionId: {
        value: plan.org_invoice_plan_id,
        label: plan.invoice_plan,
      },
    };
    navigate("/projects/create", {
      state: { presetOptions: presetOptions },
    });
    // }
  };

  return (
    <>
      {/* Mobile-responsive PolyFinTech section */}
      <div
        className={`text-center rounded-2xl relative ${isMobile ? 'p-4 mt-2' : 'mt-2'}`}
        style={{
          backgroundColor: "#2b2d42",
          color: "white",
          borderRadius: "24px",
          maxWidth: isMobile ? "100%" : "1200px",
          margin: isMobile ? "20px auto" : "40px auto",
          padding: isMobile ? "16px" : "20px",
          display: isMobile ? "block" : "flex",
          flexWrap: "wrap",
          gap: isMobile ? "20px" : "40px",
          alignItems: "flex-start",
          justifyContent: "center",
          boxShadow: isMobile ? "2px 4px 8px rgba(0,0,0,0.1)" : "5px 10px #EBECF0", // Added consistent shadow
        }}
      >
        {/* Text block */}
        <div style={{
          flex: isMobile ? "none" : "1 1 500px",
          minWidth: isMobile ? "auto" : "300px",
          textAlign: "justify",
          justifyItems: "center",
          paddingTop: isMobile ? "0" : "35px"
        }}>
          <h2 style={{
            fontSize: isMobile ? "20px" : "24px",
            marginBottom: "16px",
            letterSpacing: "2px",
            textAlign: "left",
          }}>
            About
          </h2>
          <p style={{
            fontSize: isMobile ? "14px" : "16px",
            lineHeight: "1.6",
            marginBottom: "16px",
            textAlign: "justify",
          }}>
            The objective of <strong>PolyFinTech100 API Hackathon</strong> is to nurture FinTech
            talent to meet the rapidly changing needs of the financial services sector. We invite
            students from the 5 local polytechnics to propose ideas and develop FinTech prototypes to
            address real world problem statements. Category Champions will get to showcase their ideas
            at the <strong>Singapore Fintech Festival (SFF) 2025</strong>.
          </p>
          <p style={{
            fontSize: isMobile ? "14px" : "16px",
            lineHeight: "1.6",
            textAlign: "justify",
          }}>
            The Grand Finals for PolyFinTech100 API Hackathon will be held at Singapore Fintech
            Festival 2025 Talent Stage.
          </p>
        </div>

        {/* Website Preview Block */}
        <div style={{
          flex: isMobile ? "none" : "1 1 500px",
          minWidth: isMobile ? "100%" : "300px",
          height: isMobile ? "250px" : "350px",
          borderRadius: "20px",
          overflow: "hidden",
          position: "relative",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
          marginTop: isMobile ? "20px" : "0"
        }}>
          <iframe
            src="https://polyfintech100hackathon.sg/"
            title="PolyFinTech Hackathon Preview"
            style={{
              width: "200%",
              height: "200%",
              transform: "scale(0.5)",
              transformOrigin: "0 0",
              border: "none",
              pointerEvents: "none",
            }}
          ></iframe>
          <a
            href="https://polyfintech100hackathon.sg/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "transparent",
              zIndex: 1,
              cursor: "pointer",
            }}
          ></a>
        </div>
      </div>

      {/* Mobile-responsive certification plans */}
      {Object.entries(certificationPlans).map(([category, plans]) => {
        const isGoLiveCategory =
          category.toLowerCase() ===
          InvoiceCategorySequenceString[INVOICE_CATEGORY_SEQUENCE.Go_Live].toLowerCase();

        const sortedPlans = isGoLiveCategory ? sortByCustomOrder(plans) : plans;

        return (
          <div key={category} className="category-section">
            <div className={`${isMobile ? 'mt-6 mb-4 text-center' : 'mt-4 mb-8 text-left'}`}>
              <h1
                className={`font-bold text-black ${isMobile ? 'text-xl' : 'text-2xl'}`}
                style={{ fontSize: isMobile ? "24px" : "30px" }}
              >
                {category}
              </h1>
            </div>
            <div className={`grid gap-4 mt-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 justify-center`}>
              {sortedPlans.map((plan, index) => {

                // uncomment this when cd fixes the Go Live Events Card
                // const sharedProps = {
                //   key: index,
                //   plan,
                //   currentTab,
                //   industry: plan.invoice_plan,
                //   price: plan.invoice_plan_fixed_rate,
                //   benefits: getBenefits(plan.invoice_plan, category),
                //   planId: plan.org_invoice_plan_id,
                //   onButtonClick: () => handleApplyClick(plan),
                //   buttonLabel: ["nets qr", "enets", "nets click"].some((str) =>
                //     plan.invoice_plan.toLowerCase().includes(str)
                //   )
                //     ? "Get started today"
                //     : "Apply Course Code",
                //   handleComingSoonClick,
                // };

                // comment/delete this when cd fixes the Go Live Events Card
                const isPolyFintechTest =
                  plan.invoice_plan.toLowerCase() === "polyfintech2025";

                const sharedProps = {
                  // key: index,
                  plan,
                  currentTab,
                  industry: plan.invoice_plan,
                  price: plan.invoice_plan_fixed_rate,
                  benefits: getBenefits(plan.invoice_plan, category),
                  planId: plan.org_invoice_plan_id,
                  // comment/delete this when cd fixes the Go Live Events Card
                  // onButtonClick: isPolyFintechTest
                  //   ? () => handleComingSoonClick("End June")
                  //   : () => handleApplyClick(plan),
                  onButtonClick: () => handleApplyClick(plan),
                  buttonLabel: isNetsCard(plan)
                    ? "Get started today"
                    : "Apply Voucher Code",
                  handleComingSoonClick,
                  isMobile,
                  isGoLive: plan.invoice_plan_category_id == 3,
                };

                // USE COURSECARD FOR ALL CARDS TO MAINTAIN CONSISTENCY
                return plan.invoice_plan_category_id == 8 ? (
                  <ProjectApiCatalogueCard key={index} {...sharedProps} />
                ) : isNetsCard(plan) ? (
                  <PaymentsApiCatalogueCard key={index} {...sharedProps} />
                ) : (
                  <ComboApiCatalogueCard key={index} {...sharedProps} />
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Modals */}
      {isOpen && (
        <ComingSoonModal
          setOpenModal={setOpen}
          comingSoonDate={comingSoonDate}
        />
      )}
      {isReuseProjModalOpen && (
        <RenewProjectModal
          setOpenModal={setReuseProjModalOpen}
          devProj={devProj}
          industryApiOptions={industryApiOptions}
          organisationApiOptions={organisationApiOptions}
          currentTab={currentTab}
        />
      )}
    </>
  );
}